// src/i18n/I18nProvider.tsx
import * as React from 'react';

const I18nCtx = React.createContext(null);

const STORAGE_KEY = 'app_lang';

function getInitialLang() {
  // 1) localStorage → 2) navigateur → 3) 'pl'
  // Langues supportées : pl, fr, en, es, it, de, pt, cs, nl
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return saved;
  } catch {}

  const nav = (navigator.language || 'pl').slice(0, 2);

  return ['pl', 'fr', 'en', 'es', 'it', 'de', 'pt', 'cs', 'nl'].includes(nav) ? nav : 'fr';
}

// petit utilitaire pour lire un chemin "a.b.c" dans un objet
function get(obj, path, def) {
  return String(path)
    .split('.')
    .reduce((acc, k) => (acc && typeof acc === 'object' && k in acc ? acc[k] : undefined), obj) ?? def;
}

// fusionne les tables existantes et nouvelles pour un namespace donné
function mergeTables(prevNs = {}, next = {}) {
  const langs = new Set([...Object.keys(prevNs || {}), ...Object.keys(next || {})]);
  const out = {};
  langs.forEach((lg) => {
    out[lg] = {
      ...(prevNs?.[lg] || {}),
      ...(next?.[lg] || {}),
    };
  });
  return out;
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = React.useState(getInitialLang);
  const [dict, setDict] = React.useState({});

  const setLang = (next) => {
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  };

  // Met à jour <html lang="...">
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang || 'pl');
    }
  }, [lang]);

  const register = React.useCallback((namespace, tables) => {
    if (!namespace || typeof namespace !== 'string') return;
    setDict((prev) => ({
      ...prev,
      [namespace]: mergeTables(prev[namespace], tables),
    }));
  }, []);

  const t = React.useCallback((key, fallback = '') => {
    const [ns, ...rest] = String(key).split('.');
    const path = rest.join('.');
    const tables = dict[ns];
    if (!tables) return fallback || key;

    // 1) langue courante
    const langTable = tables[lang] || {};
    const val = get(langTable, path, undefined);
    if (val !== undefined) return val;

    // 2) fallback français
    const frVal = get(tables.fr || {}, path, undefined);
    if (frVal !== undefined) return frVal;

    // 3) fallback anglais puis polonais
    const enVal = get(tables.en || {}, path, undefined);
    if (enVal !== undefined) return enVal;
    const plVal = get(tables.pl || {}, path, undefined);
    return plVal !== undefined ? plVal : (fallback || key);
  }, [dict, lang]);

  const value = React.useMemo(() => ({ lang, setLang, t, register }), [lang, t, register]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const ctx = React.useContext(I18nCtx);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>');
  return ctx;
}
